import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Modal,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  CopyOutlined,
  CrownFilled,
  DeleteOutlined,
  KeyOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useConnection } from '@sezenta/connection';
import { Contract } from '../../context/ContractHelpers';
import {
  AdminLink,
  AdminPageHeader,
  Initial,
  TEAL,
  cardShadow,
} from './adminUi';
import {
  FilterFields,
  FilterPanel,
  FilterValues,
} from '../../common/antd-filter-panel';

interface Membership {
  businessId: string;
  roleIds?: string[];
  status?: string;
  isDriver?: boolean;
}
interface AdminUser {
  userId: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  userName?: string;
  picture?: string;
  isSuperAdmin?: boolean;
  createdAt?: number | string;
  contracts?: Membership[];
}
interface Role {
  id: string;
  name?: string;
}

// A user's overall status, derived from their contract memberships.
type UserState = 'active' | 'inactive' | 'none';
const userState = (u: AdminUser): UserState => {
  if (!u.contracts?.length) return 'none';
  return u.contracts.some((c) => c.status !== 'Inactive')
    ? 'active'
    : 'inactive';
};
const STATE_META: Record<UserState, { label: string; cls: string }> = {
  active: { label: 'Active', cls: 'bg-[#E6F4F4] text-[#1A8B8F]' },
  inactive: { label: 'Inactive', cls: 'bg-[#FDECEC] text-[#C0392B]' },
  none: { label: 'No access', cls: 'bg-[#F1F2F4] text-[#84919A]' },
};
const fmtDate = (v?: number | string) =>
  v ? dayjs(Number(v)).format('DD MMM YYYY') : '—';

// A reasonably strong temporary password for admin resets: guarantees one each of
// upper/lower/digit/symbol so it passes typical policies, then fills to length.
const generatePassword = (len = 12): string => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnpqrstuvwxyz';
  const digit = '23456789';
  const symbol = '@#$%&*!?';
  const all = upper + lower + digit + symbol;
  const pick = (set: string) => set[Math.floor(Math.random() * set.length)];
  const base = [pick(upper), pick(lower), pick(digit), pick(symbol)];
  for (let i = base.length; i < len; i++) base.push(pick(all));
  // shuffle so the guaranteed chars aren't always first
  for (let i = base.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [base[i], base[j]] = [base[j], base[i]];
  }
  return base.join('');
};

// Helpful empty-state for the role dropdowns: a project with no roles can't be
// staffed, so point the admin at where to fix it instead of a bare "No data".
// `loading` covers the brief lazy-load so we never flash "no roles" prematurely.
// `projectId` (when known) is carried to the Roles page so it opens already
// scoped to the same project the admin was assigning here.
const rolesNotFound = (loading: boolean, projectId?: string) =>
  loading ? (
    <div className="py-3 text-center text-[13px] text-[#84919A]">
      Loading roles…
    </div>
  ) : (
    <div className="py-3 px-3 text-center">
      <div className="text-[13px] font-medium text-[#5E6870] mb-1">
        No roles in this project yet
      </div>
      <div className="text-[12px] text-[#84919A] leading-snug">
        Add roles in the{' '}
        <AdminLink
          to={`/admin/roles${projectId ? `?projectId=${projectId}` : ''}`}
        >
          Roles &amp; Permissions
        </AdminLink>{' '}
        section, then assign them here.
      </div>
    </div>
  );

// CR004 estimate item 21 — global users: view, add, and map to contracts with
// per-contract roles.
const GlobalUsers: React.FC = () => {
  const connection = useConnection();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [types, setTypes] = useState<
    { id: number; moduleKey?: string; extra?: { moduleKey?: string } }[]
  >([]);
  const [loading, setLoading] = useState(false);

  // search + filter (server-side)
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterValue, setFilterValue] = useState<FilterValues>([]);

  // server-side pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // add-user drawer
  const [addOpen, setAddOpen] = useState(false);
  const [addRoles, setAddRoles] = useState<Role[]>([]);
  const [addRolesLoading, setAddRolesLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [savingBid, setSavingBid] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm();

  // manage-access drawer
  const [accessUser, setAccessUser] = useState<AdminUser | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [rolesByContract, setRolesByContract] = useState<
    Record<string, Role[]>
  >({});
  const [addBid, setAddBid] = useState<string | undefined>();
  const [addRoleIds, setAddRoleIds] = useState<string[]>([]);
  const [addIsDriver, setAddIsDriver] = useState(false);

  // view drawer
  const [viewUser, setViewUser] = useState<AdminUser | null>(null);

  // reset-password modal
  const [resetUser, setResetUser] = useState<AdminUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const contractName = useCallback(
    (id: string) => contracts.find((c) => c.businessId === id)?.name ?? id,
    [contracts],
  );
  const roleName = useCallback(
    (bid: string, roleId: string) =>
      (rolesByContract[bid] ?? []).find((r) => r.id === roleId)?.name ?? roleId,
    [rolesByContract],
  );

  // Status / project filters are sent to the server (derived from the panel).
  const statusFilter = filterValue.find(
    (c) => c.name === 'status' && c.operator === 'eq',
  )?.value as 'active' | 'inactive' | 'none' | undefined;
  const contractFilter = filterValue.find(
    (c) => c.name === 'contract' && c.operator === 'eq',
  )?.value as string | undefined;
  const superAdminFilter = filterValue.find(
    (c) => c.name === 'access' && c.operator === 'eq',
  )?.value as 'super' | 'regular' | undefined;

  // Reference lists (contracts + ContractType) — small, loaded once.
  const loadMeta = useCallback(() => {
    Promise.all([
      connection.get('contracts', {
        params: { limit: 200, includeInactive: 'true' },
      }),
      connection.get('master-data/by-type/ContractType'),
    ])
      .then(([cRes, tRes]) => {
        setContracts(cRes.data?.data ?? []);
        setTypes(tRes.data?.data ?? []);
      })
      .catch(() => undefined);
  }, [connection]);

  // Server-paginated + searched + filtered users.
  const load = useCallback(() => {
    setLoading(true);
    connection
      .get('users', {
        params: {
          page,
          limit: pageSize,
          search: debouncedSearch || undefined,
          status: statusFilter,
          contract: contractFilter,
          superAdmin:
            superAdminFilter === 'super'
              ? 'true'
              : superAdminFilter === 'regular'
                ? 'false'
                : undefined,
        },
      })
      .then((r) => {
        setUsers(r.data?.data ?? []);
        setTotal(r.data?.total ?? 0);
      })
      .catch(() => message.error('Failed to load users'))
      .finally(() => setLoading(false));
  }, [
    connection,
    page,
    pageSize,
    debouncedSearch,
    statusFilter,
    contractFilter,
    superAdminFilter,
  ]);

  // Resolve a contract's module: contract.contractType → ContractType row →
  // extra.moduleKey. "isDriver" only applies to bin-bag-management contracts.
  const moduleKeyOfContract = useCallback(
    (businessId?: string): string | undefined => {
      const ct = contracts.find(
        (c) => c.businessId === businessId,
      )?.contractType;
      if (ct == null) return undefined;
      const t = types.find((x) => x.id === ct);
      return t?.moduleKey ?? t?.extra?.moduleKey;
    },
    [contracts, types],
  );

  useEffect(() => loadMeta(), [loadMeta]);
  useEffect(() => load(), [load]);

  // Debounce the search box; reset to page 1 on a new query.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Lazily load a contract's roles (used by both drawers).
  const loadRolesFor = useCallback(
    (bid?: string) => {
      if (!bid || rolesByContract[bid]) return;
      connection
        .get(`contracts/${bid}/roles`)
        .then((r) => setRolesByContract((p) => ({ ...p, [bid]: r.data ?? [] })))
        // Resolve to an empty array on failure so the "loading" state (keyed on
        // `undefined`) can't hang forever.
        .catch(() => setRolesByContract((p) => ({ ...p, [bid]: [] })));
    },
    [connection, rolesByContract],
  );

  // ---- search + filter ----
  const filterFields: FilterFields = useMemo(
    () => [
      {
        label: 'Status',
        name: 'status',
        type: 'select',
        operators: ['eq'],
        options: [
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
          { value: 'none', label: 'No access' },
        ],
      },
      {
        label: 'Project',
        name: 'contract',
        type: 'select',
        operators: ['eq'],
        options: contracts.map((c) => ({
          value: c.businessId,
          label: c.name ?? c.businessId,
        })),
      },
      {
        label: 'Access level',
        name: 'access',
        type: 'select',
        operators: ['eq'],
        options: [
          { value: 'super', label: 'Super admins' },
          { value: 'regular', label: 'Regular users' },
        ],
      },
    ],
    [contracts],
  );

  // ---- add user ----
  const openAdd = () => {
    form.resetFields();
    setAddRoles([]);
    setAddOpen(true);
  };
  const submitAdd = async () => {
    let v;
    try {
      v = await form.validateFields();
    } catch {
      return; // inline validation errors are shown by antd
    }
    setCreating(true);
    try {
      await connection.post('users', {
        email: v.email,
        userName: v.userName,
        firstName: v.firstName,
        lastName: v.lastName,
        password: v.password,
        businessId: v.businessId,
        roles: v.roles ?? [],
        isDriver: v.isDriver ?? false,
        status: 'Active',
      });
      message.success('User created');
      setAddOpen(false);
      load();
    } catch (e: any) {
      // Surface server-side field validation (e.g. "email/username already
      // exists") inline on the form AND as a toast, instead of a generic failure.
      const ve = e?.response?.data?.validationErrors as
        | Record<string, string | string[]>
        | undefined;
      if (ve && typeof ve === 'object') {
        form.setFields(
          Object.entries(ve).map(([name, errs]) => ({
            name,
            errors: (Array.isArray(errs) ? errs : [errs]).map((m) => String(m)),
          })),
        );
        const first = Object.values(ve)[0];
        message.error(
          (Array.isArray(first) ? first[0] : first) ?? 'Create failed',
        );
      } else {
        const msg = e?.response?.data?.message;
        message.error((Array.isArray(msg) ? msg[0] : msg) ?? 'Create failed');
      }
    } finally {
      setCreating(false);
    }
  };

  // ---- manage access ----
  const openAccess = (u: AdminUser) => {
    setAccessUser(u);
    setMemberships(u.contracts ?? []);
    setAddBid(undefined);
    setAddRoleIds([]);
    (u.contracts ?? []).forEach((m) => loadRolesFor(m.businessId));
  };
  const closeAccess = () => setAccessUser(null);

  const openView = (u: AdminUser) => {
    setViewUser(u);
    (u.contracts ?? []).forEach((m) => loadRolesFor(m.businessId));
  };
  const manageFromView = (u: AdminUser) => {
    setViewUser(null);
    openAccess(u);
  };

  // ---- reset password (super-admin, global) ----
  const openReset = (u: AdminUser) => {
    setResetUser(u);
    setNewPassword(generatePassword());
    setResetDone(false);
  };
  const closeReset = () => {
    setResetUser(null);
    setNewPassword('');
    setResetDone(false);
  };
  const copyPassword = () => {
    navigator.clipboard?.writeText(newPassword).then(
      () => message.success('Password copied'),
      () => undefined,
    );
  };
  const submitReset = async () => {
    if (!resetUser) return;
    if (!newPassword || newPassword.length < 6) {
      return message.warning('Password must be at least 6 characters');
    }
    setResetting(true);
    try {
      await connection.post(
        `auth/admin/users/${resetUser.userId}/reset-password`,
        { password: newPassword },
      );
      setResetDone(true);
      message.success('Password reset — share it with the user');
    } catch {
      message.error('Could not reset password');
    } finally {
      setResetting(false);
    }
  };

  const setLocalRoles = (bid: string, roleIds: string[]) =>
    setMemberships((prev) =>
      prev.map((m) => (m.businessId === bid ? { ...m, roleIds } : m)),
    );
  const setLocalDriver = (bid: string, isDriver: boolean) =>
    setMemberships((prev) =>
      prev.map((m) => (m.businessId === bid ? { ...m, isDriver } : m)),
    );

  const saveRoles = async (bid: string) => {
    const m = memberships.find((x) => x.businessId === bid);
    setSavingBid(bid);
    try {
      const body: any = { roleIds: m?.roleIds ?? [] };
      // Only send isDriver for bin-bag-management projects (collection crews).
      if (moduleKeyOfContract(bid) === 'bin-bag-management') {
        body.isDriver = m?.isDriver ?? false;
      }
      await connection.patch(
        `contracts/${bid}/users/${accessUser!.userId}/roles`,
        body,
      );
      message.success('Roles updated');
      load();
    } catch (e: any) {
      // Surface server guard messages (e.g. last-admin lockout).
      message.error(e?.response?.data?.message ?? 'Update failed');
    } finally {
      setSavingBid(null);
    }
  };

  const revoke = async (bid: string) => {
    try {
      await connection.delete(`contracts/${bid}/users/${accessUser!.userId}`);
      setMemberships((prev) => prev.filter((m) => m.businessId !== bid));
      message.success('Access removed');
      load();
    } catch (e: any) {
      // Surface server guard messages (e.g. last-admin lockout).
      message.error(e?.response?.data?.message ?? 'Remove failed');
    }
  };

  const addAccess = async () => {
    if (!addBid) return message.warning('Pick a project');
    setAdding(true);
    try {
      const isBinBag = moduleKeyOfContract(addBid) === 'bin-bag-management';
      const body: any = { roleIds: addRoleIds };
      if (isBinBag) body.isDriver = addIsDriver;
      await connection.patch(
        `contracts/${addBid}/users/${accessUser!.userId}/roles`,
        body,
      );
      setMemberships((prev) => [
        ...prev,
        {
          businessId: addBid,
          roleIds: addRoleIds,
          isDriver: isBinBag ? addIsDriver : undefined,
        },
      ]);
      setAddBid(undefined);
      setAddRoleIds([]);
      setAddIsDriver(false);
      message.success('Access granted');
      load();
    } catch {
      message.error('Grant failed');
    } finally {
      setAdding(false);
    }
  };

  const availableToAdd = contracts.filter(
    (c) => !memberships.some((m) => m.businessId === c.businessId),
  );

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_: any, u: AdminUser) => (
        <div className="flex items-center gap-2">
          <Initial name={u.name ?? u.firstName ?? u.email} src={u.picture} />
          <div>
            <div className="font-medium text-[#18191C] flex items-center gap-1.5">
              {u.name ||
                `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() ||
                u.userName}
              {u.isSuperAdmin && (
                <Tooltip title="Platform super admin — full access across all projects" placement="bottom">
                  <Tag
                    color="gold"
                    icon={<CrownFilled />}
                    className="!m-0 !text-[11px] !leading-[18px] !px-1.5"
                  >
                    Super Admin
                  </Tag>
                </Tooltip>
              )}
            </div>
            <div className="text-[12px] text-[#84919A]">{u.email}</div>
          </div>
        </div>
      ),
    },
    { title: 'Username', dataIndex: 'userName', key: 'userName' },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_: any, u: AdminUser) => {
        const m = STATE_META[userState(u)];
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium ${m.cls}`}
          >
            {m.label}
          </span>
        );
      },
    },
    {
      title: 'Projects',
      key: 'contracts',
      render: (_: any, u: AdminUser) =>
        u.contracts && u.contracts.length ? (
          <Space size={[0, 4]} wrap>
            {u.contracts.slice(0, 3).map((c) => (
              <Tag key={c.businessId} color="cyan">
                {contractName(c.businessId)}
              </Tag>
            ))}
            {u.contracts.length > 3 && <Tag>+{u.contracts.length - 3}</Tag>}
          </Space>
        ) : (
          <span className="text-[#84919A]">No access</span>
        ),
    },
    {
      title: 'Created',
      key: 'createdAt',
      width: 130,
      render: (_: any, u: AdminUser) => (
        <span className="text-[#5E6870]">{fmtDate(u.createdAt)}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      align: 'center' as const,
      render: (_: any, u: AdminUser) => (
        <Space size="middle" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Manage access" placement="bottom">
            <Button
              shape="circle"
              icon={<SettingOutlined />}
              onClick={() => openAccess(u)}
              style={{ color: TEAL, borderColor: TEAL }}
            />
          </Tooltip>
          <Tooltip title="Reset password" placement="bottom">
            <Button
              shape="circle"
              icon={<KeyOutlined />}
              onClick={() => openReset(u)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="w-full h-full p-6 overflow-auto bg-[#F7F9FB]">
      <AdminPageHeader
        title="Users"
        subtitle="All users across projects, and the projects they can access."
        action={
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={openAdd}
            style={{ background: TEAL }}
          >
            New User
          </Button>
        }
      />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-[#84919A]" />}
          placeholder="Search name, email or username"
          className="max-w-[320px]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FilterPanel
          fields={filterFields}
          value={filterValue}
          onChange={(c) => {
            setFilterValue(c ?? []);
            setPage(1);
          }}
        />
      </div>

      <div
        className="bg-white rounded-[10px] border border-[#EAECF0] p-2"
        style={cardShadow}
      >
        <Table
          rowKey="userId"
          loading={loading}
          dataSource={users}
          columns={columns}
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({
            onClick: () => openView(record),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: page,
            pageSize,
            total,
            pageSizeOptions: [10, 20, 50, 100],
            showSizeChanger: true,
            showTotal: (t) => `${t} users`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </div>

      {/* Add user */}
      <Drawer
        title="New User"
        open={addOpen}
        onClose={() => setAddOpen(false)}
        width={460}
        extra={
          <Button
            type="primary"
            loading={creating}
            onClick={submitAdd}
            style={{ background: TEAL }}
          >
            Create
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <div className="flex gap-3">
            <Form.Item
              name="firstName"
              label="First name"
              className="flex-1"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="lastName"
              label="Last name"
              className="flex-1"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </div>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: 'email' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="userName"
            label="Username"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="password"
            label="Temporary password"
            rules={[{ required: true }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="businessId"
            label="Contract"
            rules={[{ required: true, message: 'Pick a project' }]}
          >
            <Select
              placeholder="Assign to a project"
              notFoundContent={
                <div className="py-3 px-3 text-center text-[12px] text-[#84919A]">
                  No projects yet. Create one in the{' '}
                  <AdminLink to="/admin/projects">Projects</AdminLink> section.
                </div>
              }
              onChange={(v) => {
                form.setFieldsValue({ roles: [] });
                setAddRoles([]);
                setAddRolesLoading(true);
                connection
                  .get(`contracts/${v}/roles`)
                  .then((r) => setAddRoles(r.data ?? []))
                  .catch(() => setAddRoles([]))
                  .finally(() => setAddRolesLoading(false));
              }}
              options={contracts.map((c) => ({
                value: c.businessId,
                label: c.name ?? c.businessId,
              }))}
            />
          </Form.Item>
          <Form.Item name="roles" label="Roles in this project">
            <Select
              mode="multiple"
              placeholder="Select roles"
              notFoundContent={rolesNotFound(
                addRolesLoading,
                form.getFieldValue('businessId'),
              )}
              options={addRoles.map((r) => ({ value: r.id, label: r.name }))}
            />
          </Form.Item>
          {/* Driver only applies to Bin Bag Management projects (collection crews). */}
          <Form.Item noStyle shouldUpdate>
            {() =>
              moduleKeyOfContract(form.getFieldValue('businessId')) ===
              'bin-bag-management' ? (
                <Form.Item name="isDriver" valuePropName="checked">
                  <Checkbox>This user is a driver (collection crew)</Checkbox>
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </Drawer>

      {/* Manage access */}
      <Drawer
        title={`Manage access — ${accessUser?.name ?? accessUser?.email ?? ''}`}
        open={!!accessUser}
        onClose={closeAccess}
        width={500}
      >
        <div className="text-[13px] font-medium text-[#5E6870] mb-3">
          Assigned projects &amp; roles
        </div>
        {memberships.length ? (
          <div className="flex flex-col gap-3">
            {memberships.map((m) => (
              <div
                key={m.businessId}
                className="bg-[#F7F9FB] rounded-md p-3 border border-[#EAECF0]"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-[#18191C]">
                    {contractName(m.businessId)}
                  </span>
                  <Popconfirm
                    title="Remove from this project?"
                    okText="Remove"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => revoke(m.businessId)}
                  >
                    <Tooltip title="Remove from project" placement="bottom">
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                      />
                    </Tooltip>
                  </Popconfirm>
                </div>
                <div className="flex items-center gap-2">
                  {rolesByContract[m.businessId] === undefined ? (
                    // Roles still lazy-loading — show a skeleton field instead of
                    // the Select, which would otherwise flash the raw role UUIDs
                    // until the option labels arrive.
                    <Skeleton.Input
                      active
                      block
                      size="default"
                      className="flex-1"
                    />
                  ) : (
                    <>
                      <Select
                        mode="multiple"
                        className="flex-1"
                        placeholder="No roles"
                        value={m.roleIds ?? []}
                        onChange={(v) => setLocalRoles(m.businessId, v)}
                        onFocus={() => loadRolesFor(m.businessId)}
                        notFoundContent={rolesNotFound(false, m.businessId)}
                        options={(rolesByContract[m.businessId] ?? []).map(
                          (r) => ({
                            value: r.id,
                            label: r.name,
                          }),
                        )}
                      />
                      <Button
                        type="primary"
                        loading={savingBid === m.businessId}
                        onClick={() => saveRoles(m.businessId)}
                        style={{ background: TEAL }}
                      >
                        Save
                      </Button>
                    </>
                  )}
                </div>
                {moduleKeyOfContract(m.businessId) === 'bin-bag-management' && (
                  <Checkbox
                    className="mt-2"
                    checked={m.isDriver ?? false}
                    onChange={(e) =>
                      setLocalDriver(m.businessId, e.target.checked)
                    }
                  >
                    Driver (collection crew)
                  </Checkbox>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-[#84919A] text-[14px]">No project access</div>
        )}

        {availableToAdd.length > 0 && (
          <>
            <Divider />
            <div className="text-[13px] font-medium text-[#5E6870] mb-2">
              Add to a project
            </div>
            <div className="flex flex-col gap-2">
              <Select
                placeholder="Select a project"
                value={addBid}
                onChange={(v) => {
                  setAddBid(v);
                  setAddRoleIds([]);
                  loadRolesFor(v);
                }}
                options={availableToAdd.map((c) => ({
                  value: c.businessId,
                  label: c.name ?? c.businessId,
                }))}
              />
              <Select
                mode="multiple"
                placeholder="Select roles"
                value={addRoleIds}
                onChange={setAddRoleIds}
                disabled={!addBid}
                notFoundContent={
                  addBid
                    ? rolesNotFound(
                        rolesByContract[addBid] === undefined,
                        addBid,
                      )
                    : undefined
                }
                options={(addBid ? (rolesByContract[addBid] ?? []) : []).map(
                  (r) => ({ value: r.id, label: r.name }),
                )}
              />
              {moduleKeyOfContract(addBid) === 'bin-bag-management' && (
                <Checkbox
                  checked={addIsDriver}
                  onChange={(e) => setAddIsDriver(e.target.checked)}
                >
                  Driver (collection crew)
                </Checkbox>
              )}
              <Button
                type="primary"
                loading={adding}
                onClick={addAccess}
                style={{ background: TEAL }}
              >
                Add access
              </Button>
            </div>
          </>
        )}
      </Drawer>

      {/* View user */}
      <Drawer
        open={!!viewUser}
        onClose={() => setViewUser(null)}
        width={500}
        title="User details"
        extra={
          viewUser && (
            <Space>
              <Button
                icon={<KeyOutlined />}
                onClick={() => {
                  const u = viewUser;
                  setViewUser(null);
                  openReset(u);
                }}
              >
                Reset password
              </Button>
              <Button
                icon={<SettingOutlined />}
                onClick={() => manageFromView(viewUser)}
              >
                Manage access
              </Button>
            </Space>
          )
        }
      >
        {viewUser && (
          <>
            <div className="flex items-center gap-3 mb-5">
              {viewUser.picture ? (
                <img
                  src={viewUser.picture}
                  alt={viewUser.name ?? ''}
                  className="w-14 h-14 rounded-xl object-cover"
                />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-[#E6F4F4] text-[#26A3A7] flex items-center justify-center text-[24px] font-semibold">
                  {(
                    viewUser.name ??
                    viewUser.firstName ??
                    viewUser.email ??
                    '?'
                  )
                    .trim()
                    .charAt(0)
                    .toUpperCase()}
                </div>
              )}
              <div>
                <div className="text-[18px] font-semibold text-[#18191C] flex items-center gap-2">
                  {viewUser.name ||
                    `${viewUser.firstName ?? ''} ${viewUser.lastName ?? ''}`.trim() ||
                    viewUser.userName}
                  {viewUser.isSuperAdmin && (
                    <Tag color="gold" icon={<CrownFilled />}>
                      Super Admin
                    </Tag>
                  )}
                </div>
                <div className="text-[#84919A] text-[13px]">
                  {viewUser.email}
                </div>
              </div>
            </div>

            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Username">
                {viewUser.userName ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {STATE_META[userState(viewUser)].label}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {fmtDate(viewUser.createdAt)}
              </Descriptions.Item>
            </Descriptions>

            <div className="text-[13px] font-medium text-[#5E6870] mt-5 mb-2">
              Contracts &amp; roles
            </div>
            {viewUser.contracts && viewUser.contracts.length ? (
              <div className="flex flex-col gap-2">
                {viewUser.contracts.map((c) => (
                  <div
                    key={c.businessId}
                    className="bg-[#F7F9FB] rounded-md p-3 border border-[#EAECF0]"
                  >
                    <div className="font-medium text-[#18191C] mb-1">
                      {contractName(c.businessId)}
                    </div>
                    {c.roleIds && c.roleIds.length ? (
                      rolesByContract[c.businessId] === undefined ? (
                        // roles still lazy-loading — show shimmer chips (one per
                        // roleId) instead of flashing the raw UUIDs.
                        <Space size={[0, 4]} wrap>
                          {c.roleIds.map((rid) => (
                            <Skeleton.Button
                              key={rid}
                              active
                              size="small"
                              style={{
                                width: 70,
                                height: 22,
                                borderRadius: 10,
                              }}
                            />
                          ))}
                        </Space>
                      ) : (
                        <Space size={[0, 4]} wrap>
                          {c.roleIds.map((rid) => (
                            <Tag key={rid} color="cyan">
                              {roleName(c.businessId, rid)}
                            </Tag>
                          ))}
                        </Space>
                      )
                    ) : (
                      <span className="text-[#84919A] text-[12px]">
                        No roles
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[#84919A] text-[14px]">
                No project access
              </div>
            )}
          </>
        )}
      </Drawer>

      {/* Reset password */}
      <Modal
        open={!!resetUser}
        onCancel={closeReset}
        title="Reset password"
        footer={
          resetDone
            ? [
                <Button
                  key="done"
                  type="primary"
                  onClick={closeReset}
                  style={{ background: TEAL }}
                >
                  Done
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={closeReset}>
                  Cancel
                </Button>,
                <Button
                  key="reset"
                  type="primary"
                  loading={resetting}
                  onClick={submitReset}
                  style={{ background: TEAL }}
                >
                  Reset password
                </Button>,
              ]
        }
      >
        {resetUser && (
          <div className="flex flex-col gap-3 py-1">
            <div className="text-[13px] text-[#5E6870]">
              Set a new password for{' '}
              <span className="font-medium text-[#18191C]">
                {resetUser.name ||
                  `${resetUser.firstName ?? ''} ${resetUser.lastName ?? ''}`.trim() ||
                  resetUser.userName}
              </span>{' '}
              <span className="text-[#84919A]">({resetUser.email})</span>. Share
              it with them — they can change it later from their profile.
            </div>
            <Space.Compact className="w-full">
              <Input
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={resetDone}
              />
              <Tooltip title="Generate a new one" placement="bottom">
                <Button
                  icon={<ReloadOutlined />}
                  disabled={resetDone}
                  onClick={() => setNewPassword(generatePassword())}
                />
              </Tooltip>
              <Tooltip title="Copy" placement="bottom">
                <Button icon={<CopyOutlined />} onClick={copyPassword} />
              </Tooltip>
            </Space.Compact>
            {resetDone && (
              <div className="bg-[#E6F4F4] text-[#1A8B8F] text-[13px] rounded-md px-3 py-2">
                Password updated. Copy it now and share it with the user — it
                won&apos;t be shown again.
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GlobalUsers;
